import { Input } from "reactstrap"
import { IPosition } from "../../../types/position"
import { CheckGroup } from "../../../hook/useCheckGroup"
import OmsToolTip from "../../common/oms-tooltip/oms-tooltip"
import Button from "../../common/button"
import { IOrg } from "../../../types/org"
import { OrgState } from "../org-context"
import { IIndividual } from "../../../types/individual"
import { fullNameAutoFormat } from "../../../utils/format-fullname"
import { CategoryType } from "../../../types/constants"

interface CheckboxColumnProps {
  position: IPosition;
  index: number;
  isFrenchTranslator: boolean;
  checkGroup: CheckGroup<number>;
  cloned: boolean;
}

interface PositionNameColumnProps {
  position: IPosition;
  index: number;
  org: IOrg;
  positionSelected?: (position: IPosition) => void;
  state: OrgState | undefined;
}

interface PositionIndividualsItemProps {
  position: IPosition;
  org: IOrg;
  positionSelected?: (position: IPosition) => void;
  individualSelected?: (individual: IIndividual, positionRole:IPosition) => void;
  lastSelectedIndividual: (individual: IIndividual, positionRole:IPosition) => any;
  state: OrgState | undefined;
  isFrenchTranslator: boolean;
  checkGroup: CheckGroup<number>;
  cloned: boolean;
  index: number;
  getPositionName?: (positionId: number) => string;
}

interface ClickableIndividualProps {
  position: IPosition;
  individual: IIndividual;
  individualSelected?: (individual: IIndividual, positionRole:IPosition) => void;
  lastSelectedIndividual: (individual: IIndividual, positionRole:IPosition) => any;
  state: OrgState | undefined;
}

function isOrganization(org: IOrg): boolean {
  return org.details?.category.type === CategoryType.ORGANIZATION;
}

function formatAssignmentType(individual: IIndividual): string {
  // returns "Secondment (Acting)" or "Home"
  return individual.acting
    ? `${individual.assignmentType} (Acting)`
    : individual.assignmentType;
}

function formatIndividualName(individual: IIndividual): string {
  const name = fullNameAutoFormat(individual.firstName, individual.middleName, individual.lastName);

  // return "John Smith (Inactive)" or "John Smith"
  return individual.indStatus.toLowerCase() === "inactive"
    ? `${name} (${individual.indStatus})`
    : name;
}

function managementRowCssClass(position: IPosition): string {
  return position.managementIndicator ? "fw-bold" : "";
}

function formatVacantLabel(position: IPosition, org: IOrg): string {
  // this could be simplified to a nested ternary operator, but they can be hard to read, so we're leaving this more simplified
  if (position.isRole) {
    return isOrganization(org) ? "Vacant" : "Position Based";
  }

  return "Vacant";
}

function formatVacantPositionName(position: IPosition, org: IOrg, getPositionName?: (positionId: number) => string): string {
  if (position.isRole) {
    if (!isOrganization(org) && position.childId && getPositionName) {
      return getPositionName(position.childId);
    }
  }
  return "";
}

function formatRoleAssignmentType(position: IPosition, individual: IIndividual): string {
  // this could be simplified to a nested ternary operator, but they can be hard to read, so we're leaving this more simplified
  if (position.isRole) {
    return individual.assignmentType && individual.assignmentType.toLowerCase() === "position based"
      ? individual.positionName
      : "";
  }

  return "";
}

// NOTE: Roles and Positions have different business meanings, but in the database they're the same thing, just differentiated with a flag on the record
// the type is IPosition for both, so we're using position for the variable name, even if its a role

function CheckboxColumn(props: CheckboxColumnProps) : React.JSX.Element {
  // if a position/role has multiple individuals in it, the first one will render a <td>, the rest of the rows won't render the td tag at all
  if (props.index > 0) {
      return (<></>);
  }

  // French translators don't see the checkboxe column at all
  if (props.isFrenchTranslator) {
    return (<></>);
  }

  if (props.checkGroup.isEmpty()) {
    return (<td rowSpan={props.position.individuals?.length ?? 1}></td>);
  }

  // co-ordinators/admins can select the rows
  return (
      <td rowSpan={props.position.individuals?.length ?? 1}>
      <Input
          className="form-check-input"
          type="checkbox"
          checked={props.checkGroup.isChecked(props.position.positionId)}
          onChange={event => props.checkGroup.onCheck(event, props.position.positionId)}
          aria-label={`select ${props.position.positionName}`}
          disabled={props.cloned}
      />
      </td>
  );
}

function PositionNameColumn(props: PositionNameColumnProps) : React.JSX.Element {
  // if a position/role has multiple individuals in it, the first one will render a <td>, the rest of the rows won't render the td tag at all
  if (props.index > 0) {
    return (<></>);
  }

  // everyone sees the tooltip/button, once per position/role
  return (
    <td rowSpan={props.position.individuals?.length ?? 1}>
      <OmsToolTip id={`op_${props.org.id}_tooltip_${props.position.positionId}`} orgId={props.org.id}>
        <Button
          color={props.positionSelected ? "link" : ""}
          className={`oms-button-link ${props.state?.lastSelectedItem === props.position ? "oms-button-link-selected" : "" }`}
          onClick={()=>{ props.positionSelected && props.positionSelected(props.position) }}>{props.position.fullPositionName}</Button>
      </OmsToolTip>
    </td>
  );
}

function ClickableIndividualColumn({position, individual, state, lastSelectedIndividual, individualSelected}: ClickableIndividualProps): React.JSX.Element {
  const roleAssignmentType = formatRoleAssignmentType(position, individual);
  // we want the role assignment type to be below the button, if its visible
  return (
    <td>
      <Button
        color={individualSelected ? "link" : ""}
        className={`oms-button-link ${state?.lastSelectedItem === lastSelectedIndividual(individual, position) ? "oms-button-link-selected" : "" }`}
        onClick={()=>{ individualSelected && individualSelected(individual, position)}}>{ formatIndividualName(individual) }
      </Button>
      { roleAssignmentType && <><br />{ roleAssignmentType }</>  }
    </td>
  );
}

// this handles both positions AND roles
// roles have a bit of extra logic
export function PositionIndividualsItem(props: PositionIndividualsItemProps) : React.JSX.Element {
  // position/role is vacant
  if (!props.position.individuals || props.position.individuals.length === 0) {
    return (
      <tbody>
        <tr key={props.position.positionId} className={managementRowCssClass(props.position)}>
          <CheckboxColumn position={props.position} index={0} isFrenchTranslator={props.isFrenchTranslator} checkGroup={props.checkGroup} cloned={props.cloned} />
          <PositionNameColumn position={props.position} index={0} org={props.org} state={props.state} positionSelected={props.positionSelected} />
          <td>{ formatVacantLabel(props.position, props.org) }</td>
          <td>{ formatVacantPositionName(props.position, props.org, props.getPositionName) }</td>
        </tr>
      </tbody>
    );
  }
  // single individual in the position/role - most common case
  if (props.position.individuals.length === 1) {
    return (
      <tbody>
        <tr key={props.position.positionId} className={managementRowCssClass(props.position)}>
          <CheckboxColumn position={props.position} index={0} isFrenchTranslator={props.isFrenchTranslator} checkGroup={props.checkGroup} cloned={props.cloned} />
          <PositionNameColumn position={props.position} index={0} org={props.org} state={props.state} positionSelected={props.positionSelected} />
          <td>{ formatAssignmentType(props.position.individuals[0]) }</td>
          <ClickableIndividualColumn position={props.position} individual={props.position.individuals[0]} lastSelectedIndividual={props.lastSelectedIndividual} individualSelected={props.individualSelected} state={props.state} />
        </tr>
      </tbody>
    )
  }
  else {
    // multiple individuals in position/role, can happen if someone is on leave
    return (
      <tbody className={managementRowCssClass(props.position)}>
        { props.position.individuals.map((individual: IIndividual, index: number) => (
          <tr key={`${props.position.positionId}.${individual.individualId}`} >
            <CheckboxColumn position={props.position} index={index} isFrenchTranslator={props.isFrenchTranslator} checkGroup={props.checkGroup} cloned={props.cloned} />
            <PositionNameColumn position={props.position} index={index} org={props.org} state={props.state} positionSelected={props.positionSelected} />
            <td>{ formatAssignmentType(individual) }</td>
            <ClickableIndividualColumn position={props.position} individual={individual} lastSelectedIndividual={props.lastSelectedIndividual} individualSelected={props.individualSelected} state={props.state} />
          </tr>
        ))}
      </tbody>
    )
  }
}
